import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { List } from '@fuww/library/src/List';
import Anchor from '@fuww/library/src/Anchor';
import { EventListItem } from '@fuww/library/src/Events';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import { Link } from '../../routes.mjs';
import formatDateWithLocale from '../../lib/formatDateWithLocale';
import { useSiteContext } from '../SiteContext';
import getEventJsonLdData from '../../lib/getEventJsonLdData';
import eventsConnectionQuery
from '../../lib/queries/events/eventsConnectionQuery.graphql';
import withQuery from '../../lib/withQuery';
import loadMore from '../../lib/queries/loadMore';
import LoadMore from '../LoadMore';

export const getInterval = (start, end, locale) => {
  if (!start || !end) {
    return '';
  }

  const startFormatted = formatDateWithLocale(start, locale, {
    timeZone: 'Europe/Berlin',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const endFormatted = formatDateWithLocale(end, locale, {
    timeZone: 'Europe/Berlin',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return `${startFormatted} - ${endFormatted}`;
};

const EventList = ({
  loading, variables, fetchMore, data, showLoader,
}) => {
  const { hasNextPage = false } = data?.eventsConnection?.pageInfo ?? {};
  const intl = useIntl();
  const { locale, url } = useSiteContext();

  const rows = data?.eventsConnection?.edges?.map(({ node: event }) => {
    const {
      title,
      slug,
      editions,
    } = event;

    const now = new Date();
    const upcomingEdition = editions.find(
      (edition) => new Date(edition.startsAt) >= now,
    );
    const relevantEdition = upcomingEdition ?? editions.at(-1);

    const start = new Date(relevantEdition.startsAt);
    const end = new Date(relevantEdition.endsAt);
    const location = [relevantEdition.buildingName, relevantEdition.city]
      .filter(Boolean)
      .join(', ');
    const addressCountry = relevantEdition.country;

    return (
      <Fragment key={slug}>
        <li>
          <Link
            route="event"
            params={{ slug }}
            passHref
          >
            <Anchor styled>
              <EventListItem
                showLoader={showLoader}
                showDivider
                jsonLd={(
                  <script
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: getEventJsonLdData(
                        intl,
                        {
                          ...event,
                          startDate: start,
                          endDate: end,
                        },
                        url,
                        event.imageUrl,
                      ),
                    }}
                  />
                )}
                day={formatDateWithLocale(start, locale, {
                  timeZone: 'Europe/Berlin',
                  day: 'numeric',
                })}
                month={formatDateWithLocale(start, locale, {
                  timeZone: 'Europe/Berlin',
                  month: 'short',
                })?.toUpperCase()}
                title={title}
                interval={getInterval(start, end, locale)}
                location={location}
                addressCountry={addressCountry}
              />
            </Anchor>
          </Link>
        </li>
      </Fragment>
    );
  }) ?? Array.from({ length: 3 }, (_, index) => (
    <Fragment key={`skeleton-${index}`}>
      <li>
        <EventListItem
          showLoader
          jsonLd={<div />}
          day="00"
          month="MMM"
        />
      </li>
    </Fragment>
  ));

  return (
    <>
      <List className="fu-mdc-list">
        {rows}
      </List>
      <LoadMore
        loading={loading}
        hasNextPage={hasNextPage}
        fetchMore={
        () => loadMore({
          variables,
        })(data, 'eventsConnection', fetchMore)
      }
      />
    </>
  );
};

EventList.propTypes = {
  loading: PropTypes.bool,
  variables: PropTypes.shape({}),
  fetchMore: PropTypes.func,
  data: PropTypes.shape({
    eventsConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({ node: PropTypes.shape({}) })),
      pageInfo: PropTypes.shape({
        hasNextPage: PropTypes.bool,
        endCursor: PropTypes.string,
      }),
    }),
  }),
  showLoader: PropTypes.bool,
};

EventList.defaultProps = {
  loading: true,
  variables: {},
  fetchMore: undefined,
  data: {},
  showLoader: false,
};

const EventListWithQuery = withQuery(EventList, { renderAlways: true });

const EventListWithProperties = (properties) => (
  <EventListWithQuery
    {...properties}
    query={eventsConnectionQuery}
    errorMessage="Error loading events"
    loader={<HiddenLoader />}
  />
);

export default EventListWithProperties;
